<template>
  <!-- Breadcrumb Navigation -->
  <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
    <router-link :to="{ name: 'pages.admin.dashboard' }"
      class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <icon icon="solar:home-smile-bold-duotone" />
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
    </router-link>
    <span class="tw-text-gray-300">/</span>
    <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.videos", "Videos") }}</span>
    </button>
  </div>

  <div class="tw-p-2">
    <div class="tw-h-full tw-w-full tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">

      <!-- Filters row (cloned from Users) -->
      <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <div class="tw-flex tw-items-center tw-gap-2">
          <FormField
            v-model="search"
            type="text"
            :hide-label="true"
            :hide-errors="true"
            :placeholder="$t('videos.search-placeholder', 'Search by title...')"
            left-icon="solar:magnifer-linear"
            @input="onSearch"
            class="tw-bg-white tw-rounded-md tw-min-w-[300px]"
          />

          <FormSelect
            v-model="filters.category"
            :options="categoryOptions"
            class="tw-bg-white tw-rounded-md tw-min-w-[150px]"
          />

          <FormSelect
            v-model="filters.status"
            :options="statusOptions"
            class="tw-bg-white tw-rounded-md tw-min-w-[150px]"
          />
        </div>

        <div>
          <a-button
            as="router-link"
            :to="{ name: 'pages.admin.videos.create' }"
            intent="flat"
            left-icon="solar:add-circle-bold-duotone"
          >
            {{ $t('videos.create-new', 'Add Video') }}
          </a-button>
        </div>
      </div>

      <!-- Table -->
      <VideoTable
        :loading="loading"
        :items="videos"
        :options="paginationOptions"
        @next="onNext"
        @previous="onPrevious"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import VideoTable from '@/views/admin/videos/partials/table/VideoTable';
import videosApi from '@/api/videos';
import appApi from '@/api/app';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

const loading = ref(false);
const videos = ref([]);
const search = ref('');

const paginationOptions = ref({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null
});

const filters = reactive({
    category: '',
    status: ''
});

const categoryOptions = ref([{ id: '', name: t('videos.all-categories', 'All categories') }]);
const statusOptions = ref([
    { id: '', name: t('videos.all-statuses', 'All statuses') },
    { id: 'active', name: t('common.active', 'Active') },
    { id: 'inactive', name: t('common.inactive', 'Inactive') }
]);

let searchTimeout = null;
let filterTimeout = null;

watch(() => filters.category, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => fetchVideos(1), 300);
});
watch(() => filters.status, () => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => fetchVideos(1), 300);
});

async function fetchVideos(page = 1) {
    loading.value = true;
    videos.value = [];
    try {
        const params = {
            page,
            per_page: paginationOptions.value.per_page,
            search: search.value,
            ...(filters.category && { category_id: filters.category }),
            ...(filters.status && { status: filters.status })
        };

        const response = await videosApi.admin.paginate(params);
        const data = response.data?.data;

        if (data && Array.isArray(data.data)) {
            videos.value = data.data;
            paginationOptions.value = {
                current_page: data.current_page,
                per_page: data.per_page,
                total: data.total,
                last_page: data.last_page,
                next_page_url: data.next_page_url,
                prev_page_url: data.prev_page_url
            };
        }
    } catch (error) {
        console.error('Error fetching videos:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
    } finally {
        loading.value = false;
    }
}

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => fetchVideos(1), 500);
};

const onNext = () => {
    if (paginationOptions.value.current_page < paginationOptions.value.last_page) {
        fetchVideos(paginationOptions.value.current_page + 1);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.current_page > 1) {
        fetchVideos(paginationOptions.value.current_page - 1);
    }
};

const fetchCategories = async () => {
    try {
        const response = await appApi.forms.categories('video');
        categoryOptions.value = [
            { id: '', name: t('videos.all-categories', 'All categories') },
            ...(response.data || []).map(c => ({ id: c.id, name: c.name }))
        ];
    } catch (error) {
        console.error('Error fetching categories:', error);
    }
};

// Let the row/actions components remove a deleted video from the list.
const onDeleted = (id) => {
    const index = videos.value.findIndex(v => v.id === id);
    if (index !== -1) videos.value.splice(index, 1);
};
provide('onDeleted', onDeleted);

onMounted(() => {
    fetchVideos();
    fetchCategories();
});
</script>

<style></style>
